import { AxiosInstance } from "axios";
import {
  ActivateInactivateUserInput,
  CreatePersonInput,
  CreateUserFromPersonInput,
  CreateUserInput,
  PersonFilter,
  UpdatePersonInput,
} from "../components/routeComponents/Persons/Person";
import {
  GrantRoleAssignmentsInput,
  RevokeRoleAssignmentsInput,
  getPersonsGateway,
  getRoles,
  grantRoleAssigmentsMutation,
  revokeRoleAssigmentsMutation,
  getPersonQuery,
  getSupervisorsQuery,
  getContractingCompaniesQuery,
  createPersonMutation,
  updatePerson,
  createUserWithPersonMutation,
  createUserFromPerson,
  updateUser,
  getPersonIdQuery,
} from "../components/supportFunctions/graphql/PersonQueries";
import {
  PaginationInput,
  PaginationQueryObject,
} from "../components/supportFunctions/graphql/Queries";
import BaseService from "./BaseService";
import {
  getUserQuery,
  usersListQuery,
} from "../components/supportFunctions/graphql/UserQueries";
import { getCustomerApplicationsQuery } from "../components/supportFunctions/graphql/CustomerQueries";
import {
  GrantCustomerApplicationsInput,
  RemoveCustomerApplicationsInput,
  UpdateCustomerApplicationUrlInput,
  editCustomerApplicationMutation,
  grantCustomerApplicationAssignmentsMutation,
  removeCustomerApplicationAssignmentsMutation,
} from "../components/supportFunctions/graphql/ApplicationsQueries";
import UsersService from "@velocity-ehs/user-utils-module";
import {
  cloneRoleQuery,
  createCustomerRole,
  customerRoles,
  rolePoliciesQuery,
  updateRole,
  userRoleLocationCountQuery,
  userRoleLocationDetailsQuery,
} from "../components/supportFunctions/graphql/RolesQueries";
import {
  GetUserRoleLocationCountFilter,
  GetUsersAssignedToRoleFilter,
} from "../components/routeComponents/RoleManagement/Utils";
import {
  createLocationMutation,
  locationDetailsQuery,
  locationUpdateMutation,
} from "../components/supportFunctions/graphql/LocationsQueries";
import {
  CreateLocationInput,
  UpdateLocationInput,
} from "../components/routeComponents/Locations/Location";
import {
  ContractingCompanyFilter,
  CreateContractingCompanyInput,
  UpdateContractingCompanyInput,
} from "../components/routeComponents/ContractingCompanies/Utils";
import {
  contractingCompaniesQuery,
  createContractingCompanyMutation,
  updateContractingCompanyMutation,
} from "../components/supportFunctions/graphql/ContractingCompaniesQueries";
import { policiesQuery } from "../components/supportFunctions/graphql/PoliciesQueries";
import { PolicyFilter } from "../hooks/useGetPolicies";
import {
  CloneRoleInput,
  UpdateRoleInput,
  CreateCustomerRoleInput,
} from "../hooks/useRoleMutations";
import {
  LocationApplicationsBundleInput,
  locationApplicationsBundleMutation,
} from "../components/supportFunctions/graphql/ApplicationBundleQueries";

class GatewayService extends BaseService {
  public api: AxiosInstance;

  constructor() {
    super(process.env.GRAPHQL_GATEWAY_URL, true);
  }

  async getPersons(
    filter: PersonFilter,
    paginationInput: PaginationQueryObject
  ) {
    try {
      const result = await this.api.post(
        "",
        getPersonsGateway(filter, paginationInput)
      );

      return result.data.data.persons;
    } catch (error) {
      throw error;
    }
  }

  async getPerson(id: string) {
    try {
      const response = await this.api.post("", getPersonQuery(id));

      return response.data.data.person;
    } catch (error) {
      throw error;
    }
  }

  async getUserProfile(filter: any) {
    try {
      const result = await this.api.post("", getUserQuery(filter));

      return result.data.data.user;
    } catch (error) {
      throw error;
    }
  }

  async getUsers(filter: any, pagination: PaginationQueryObject) {
    try {
      const result = await this.api.post(
        "",
        usersListQuery(filter, pagination)
      );
      return result.data.data.users;
    } catch (error) {
      throw error;
    }
  }

  async getRoles(
    userId: string,
    filter: any,
    paginationInput: PaginationQueryObject
  ) {
    try {
      const result = await this.api.post(
        "",
        getRoles(userId, filter, paginationInput)
      );

      return result.data.data.user.roleAssignments;
    } catch (error) {
      throw error;
    }
  }

  async assignRoles(values: GrantRoleAssignmentsInput[]) {
    try {
      const result = await this.api.post(
        "",
        grantRoleAssigmentsMutation(values)
      );
      return result;
    } catch (error) {
      throw error;
    }
  }

  async revokeRoles(values: RevokeRoleAssignmentsInput[]) {
    try {
      const result = await this.api.post(
        "",
        revokeRoleAssigmentsMutation(values)
      );
      return result;
    } catch (error) {
      throw error;
    }
  }

  async getCustomerApplications(
    customerId: string,
    paginationInput: PaginationQueryObject
  ) {
    try {
      const result = await this.api.post(
        "",
        getCustomerApplicationsQuery(customerId, null, paginationInput)
      );

      return result.data.data.customer.applications;
    } catch (error) {
      throw error;
    }
  }

  async editCustomerApplication(input: UpdateCustomerApplicationUrlInput) {
    try {
      await this.api.post("", editCustomerApplicationMutation(input));

      return true;
    } catch (error) {
      throw error;
    }
  }

  async assignCustomerApplication(input: GrantCustomerApplicationsInput) {
    try {
      await this.api.post(
        "",
        grantCustomerApplicationAssignmentsMutation(input)
      );

      return true;
    } catch (error) {
      throw error;
    }
  }

  async unassignCustomerApplication(input: RemoveCustomerApplicationsInput) {
    try {
      await this.api.post(
        "",
        removeCustomerApplicationAssignmentsMutation(input)
      );

      return true;
    } catch (error) {
      throw error;
    }
  }

  async getSupervisors() {
    try {
      const response = await this.api.post(
        "",
        getSupervisorsQuery(UsersService.customer?.id, { pageSize: 10000 })
      );

      return response.data.data.persons;
    } catch (error) {
      throw error;
    }
  }

  async getContractingCompanies() {
    try {
      const response = await this.api.post(
        "",
        getContractingCompaniesQuery(UsersService.customer?.id, {
          pageSize: 10000,
        })
      );

      return response.data.data.contractingCompanies;
    } catch (error) {
      throw error;
    }
  }

  async createPerson(input: CreatePersonInput) {
    try {
      const response = await this.api.post("", createPersonMutation(input));

      return response.data.data.createPerson;
    } catch (error) {
      throw error;
    }
  }

  async updatePerson(input: UpdatePersonInput) {
    try {
      const response = await this.api.post("", updatePerson({ input }));

      return response.data.data.updatePerson;
    } catch (error) {
      throw error;
    }
  }

  async createUserWithPerson(input: CreateUserInput) {
    try {
      const response = await this.api.post(
        "",
        createUserWithPersonMutation(input)
      );

      return response.data.data.createUserWithPerson;
    } catch (error) {
      throw error;
    }
  }

  async createUserFromPerson(input: CreateUserFromPersonInput) {
    const { firstName, lastName, ...body } = input;

    try {
      //Remove this call when BE fixes the create user from person logic.
      await this.api.post(
        "",
        updatePerson({
          input: {
            id: input.personId,
            firstName,
            lastName,
          },
        })
      );

      const response = await this.api.post("", createUserFromPerson(body));

      return response.data.data.createUserFromPerson;
    } catch (error) {
      throw error;
    }
  }

  async activateInactivateUser(input: ActivateInactivateUserInput) {
    try {
      const response = await this.api.post("", updateUser(input));

      return response.data.data.updateUser;
    } catch (error) {
      throw error;
    }
  }

  async getUserRoleLocationCount(
    filter: GetUserRoleLocationCountFilter,
    paginationInput: PaginationQueryObject
  ) {
    try {
      const result = await this.api.post(
        "",
        userRoleLocationCountQuery(paginationInput, filter)
      );

      return result.data.data.userRoleLocationCount;
    } catch (error) {
      throw error;
    }
  }

  async getUsersAssignedToRole(
    filter: GetUsersAssignedToRoleFilter,
    paginationInput: PaginationQueryObject
  ) {
    try {
      const result = await this.api.post(
        "",
        userRoleLocationDetailsQuery(paginationInput, filter)
      );
      return result.data.data.userRoleLocationDetails;
    } catch (error) {
      throw error;
    }
  }

  async getPersonId(userId: string) {
    try {
      const result = await this.api.post("", getPersonIdQuery(userId));

      return result.data.data.user;
    } catch (error) {
      throw error;
    }
  }

  async getLocation(locationId: string) {
    try {
      const result = await this.api.post("", locationDetailsQuery(locationId));

      return result.data.data.location;
    } catch (error) {
      throw error;
    }
  }

  async createLocation(input: CreateLocationInput) {
    try {
      const result = await this.api.post("", createLocationMutation(input));

      return result.data.data.createLocation;
    } catch (error) {
      throw error;
    }
  }

  async updateLocation(input: UpdateLocationInput) {
    try {
      const result = await this.api.post("", locationUpdateMutation(input));

      return result.data.data.createLocation;
    } catch (error) {
      throw error;
    }
  }

  async getContractingCompaniesQuery(
    filter: ContractingCompanyFilter,
    paginationInput: PaginationQueryObject
  ) {
    try {
      const result = await this.api.post(
        "",
        contractingCompaniesQuery(filter, paginationInput)
      );

      return result.data.data.contractingCompanies;
    } catch (error) {
      throw error;
    }
  }

  async createContractingCompany(input: CreateContractingCompanyInput) {
    try {
      const result = await this.api.post(
        "",
        createContractingCompanyMutation(input)
      );

      return result.data.data.createContractingCompany;
    } catch (error) {
      throw error;
    }
  }

  async updateContractingCompany(input: UpdateContractingCompanyInput) {
    try {
      const result = await this.api.post(
        "",
        updateContractingCompanyMutation(input)
      );

      return result.data.data.pdateContractingCompany;
    } catch (error) {
      throw error;
    }
  }

  async getCustomerRoles(
    id: string,
    filter: any,
    paginationInput: PaginationQueryObject
  ) {
    try {
      const result = await this.api.post(
        "",
        customerRoles(id, filter, paginationInput)
      );

      return result.data.data.customer.roles;
    } catch (error) {
      throw error;
    }
  }

  async getCustomerRole(id: string, filter: any) {
    try {
      const result = await this.api.post("", customerRoles(id, filter));

      return result.data.data.customer.roles;
    } catch (error) {
      throw error;
    }
  }

  async getRolePolicies(
    id: string,
    filter: any,
    paginationInput: PaginationQueryObject
  ) {
    try {
      const result = await this.api.post(
        "",
        rolePoliciesQuery(id, filter, paginationInput)
      );

      return result.data.data.customer.roles;
    } catch (error) {
      throw error;
    }
  }

  async getPolicies(
    filter: PolicyFilter,
    paginationInput: PaginationQueryObject
  ) {
    try {
      const result = await this.api.post(
        "",
        policiesQuery(filter, paginationInput)
      );

      return result.data.data.policies;
    } catch (error) {
      throw error;
    }
  }

  async updateRole(input: UpdateRoleInput) {
    try {
      const result = await this.api.post("", updateRole(input));

      return result.data.data.updateRole;
    } catch (error) {
      throw error;
    }
  }

  async cloneRole(input: CloneRoleInput) {
    try {
      const result = await this.api.post("", cloneRoleQuery(input));

      return result.data.data.cloneRole;
    } catch (error) {
      throw error;
    }
  }

  async createCustomerRole(input: CreateCustomerRoleInput) {
    try {
      const result = await this.api.post("", createCustomerRole(input));

      return result.data.data.createCustomerRole;
    } catch (error) {
      throw error;
    }
  }

  async setLocationApplicationBundle(input: LocationApplicationsBundleInput) {
    try {
      const result = await this.api.post(
        "",
        locationApplicationsBundleMutation(input)
      );

      return result.data.data.location;
    } catch (error) {
      throw error;
    }
  }
}

export default new GatewayService();
